import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { CartLineItem } from '@adapters/bigcommerce/types/cart';
import { LocalStorageSaveForLaterItem, VariantDetailsData } from './types';
import {
  SAVE_FOR_LATER_ITEMS_KEY,
  saveItemsToLocalStorage,
} from './utils/saveItemsToLocalStorage';

export interface State {
  items: LocalStorageSaveForLaterItem[];
  addindToCartLoading: boolean;
  saveForLaterItemAddedToCart: boolean;
  errorAddingToLocalStorage: { cartItemId: number } | null;
  saveForLaterItemToAddToCart: VariantDetailsData | null;
  scrollToTop: boolean;
}

export interface Actions {
  addItemToSaveForLater: (
    product_id: CartLineItem['product_id'],
    variant_id: CartLineItem['variant_id']
  ) => void;
  removeSaveForLaterItem: (variantId: number) => void;
  onAddToCart: () => void;
  onAddToSaveForLater: () => void;
  addingToCart: (boolean: boolean) => void;
  setSaveForLaterItemToAddToCart: (item: VariantDetailsData | null) => void;
}

export type SaveForLaterState = State & Actions;

export enum ActionType {
  ON_ADD_TO_CART = 'ON_ADD_TO_CART',
  ON_ADD_TO_SAVE_FOR_LATER = 'ON_ADD_TO_SAVE_FOR_LATER',
  ADDING_TO_CART = 'ADDING_TO_CART',
  SET_SAVE_FOR_LATER_ITEM_TO_ADD_TO_CART = 'SET_SAVE_FOR_LATER_ITEM_TO_ADD_TO_CART',
  ADD_ERROR_ADDING_TO_LOCAL_STORAGE = 'ADD_ERROR_ADDING_TO_LOCAL_STORAGE',
  UPDATE_SAVE_FOR_LATER_ITEMS = 'UPDATE_SAVE_FOR_LATER_ITEMS',
}

type Action =
  | { type: ActionType.ON_ADD_TO_CART }
  | { type: ActionType.ON_ADD_TO_SAVE_FOR_LATER }
  | { type: ActionType.ADDING_TO_CART; boolean: boolean }
  | {
      type: ActionType.SET_SAVE_FOR_LATER_ITEM_TO_ADD_TO_CART;
      item: VariantDetailsData | null;
    }
  | { type: ActionType.ADD_ERROR_ADDING_TO_LOCAL_STORAGE; cartItemId: number }
  | {
      type: ActionType.UPDATE_SAVE_FOR_LATER_ITEMS;
      items: LocalStorageSaveForLaterItem[];
    };

const initialState: SaveForLaterState = {
  items: [],
  addindToCartLoading: false,
  saveForLaterItemAddedToCart: false,
  errorAddingToLocalStorage: null,
  saveForLaterItemToAddToCart: null,
  scrollToTop: false,
  addItemToSaveForLater: () => null,
  removeSaveForLaterItem: () => null,
  onAddToCart: () => null,
  onAddToSaveForLater: () => null,
  addingToCart: () => null,
  setSaveForLaterItemToAddToCart: () => null,
};

export const SaveForLaterContext =
  createContext<SaveForLaterState>(initialState);

SaveForLaterContext.displayName = 'SaveForLaterContext';

const saveForLaterReducer = (state: State, action: Action) => {
  switch (action.type) {
    case ActionType.UPDATE_SAVE_FOR_LATER_ITEMS: {
      return {
        ...state,
        items: action.items,
      };
    }
    case ActionType.ON_ADD_TO_CART: {
      return {
        ...state,
        saveForLaterItemAddedToCart: true,
        scrollToTop: !state.scrollToTop,
      };
    }
    case ActionType.ON_ADD_TO_SAVE_FOR_LATER: {
      return {
        ...state,
        saveForLaterItemAddedToCart: false,
      };
    }
    case ActionType.ADDING_TO_CART: {
      return {
        ...state,
        addindToCartLoading: action.boolean,
      };
    }
    case ActionType.SET_SAVE_FOR_LATER_ITEM_TO_ADD_TO_CART: {
      return {
        ...state,
        setSaveForLaterItemToAddToCart: action.item,
      };
    }
    case ActionType.ADD_ERROR_ADDING_TO_LOCAL_STORAGE: {
      return {
        ...state,
        errorAddingToLocalStorage: { cartItemId: action.cartItemId },
      };
    }
    default: {
      return { ...state };
    }
  }
};

export interface SaveForLaterContextProviderProps {
  children: ReactNode;
}

export const SaveForLaterContextProvider = ({
  children,
}: SaveForLaterContextProviderProps) => {
  const [state, dispatch] = useReducer(saveForLaterReducer, initialState);

  const addErrorAddingToLocalStorage = (cartItemId: number) => {
    dispatch({
      type: ActionType.ADD_ERROR_ADDING_TO_LOCAL_STORAGE,
      cartItemId,
    });
  };

  const addItemToSaveForLater = useCallback(
    (
      product_id: CartLineItem['product_id'],
      variant_id: CartLineItem['variant_id']
    ) => {
      const newItem: LocalStorageSaveForLaterItem = {
        productId: product_id,
        variantId: variant_id,
      };
      const items = [newItem, ...state.items];
      const removeDuplicateNewItems = items.filter(
        (value, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.productId === value.productId && t.variantId === value.variantId
          )
      );
      const savedToLocalStorage = saveItemsToLocalStorage(
        removeDuplicateNewItems
      );
      if (!savedToLocalStorage) {
        addErrorAddingToLocalStorage(variant_id);
      } else {
        dispatch({
          type: ActionType.UPDATE_SAVE_FOR_LATER_ITEMS,
          items: removeDuplicateNewItems,
        });
      }
    },
    [state.items]
  );

  const removeSaveForLaterItem = useCallback(
    (variantId: number) => {
      if (!variantId) return;
      const newState = { ...state };
      const items = newState.items.filter(
        (item: { variantId: number }) => item.variantId !== variantId
      );

      saveItemsToLocalStorage(items);

      dispatch({ type: ActionType.UPDATE_SAVE_FOR_LATER_ITEMS, items });
    },
    [state]
  );

  const onAddToCart = () => {
    dispatch({ type: ActionType.ON_ADD_TO_CART });
  };

  const onAddToSaveForLater = () => {
    dispatch({ type: ActionType.ON_ADD_TO_SAVE_FOR_LATER });
  };

  const addingToCart = (boolean: boolean) => {
    dispatch({ type: ActionType.ADDING_TO_CART, boolean });
  };

  const setSaveForLaterItemToAddToCart = (item: VariantDetailsData | null) => {
    dispatch({ type: ActionType.SET_SAVE_FOR_LATER_ITEM_TO_ADD_TO_CART, item });
  };

  const loadFromLocalStorage = () => {
    let stored;
    try {
      stored = localStorage.getItem(SAVE_FOR_LATER_ITEMS_KEY);
    } catch (error) {
      console.error(
        'Error loading items from localStorage FOR Save For Later:',
        error
      );
    }
    if (stored) {
      try {
        const items = JSON.parse(stored);
        if (items.length) {
          dispatch({ type: ActionType.UPDATE_SAVE_FOR_LATER_ITEMS, items });
        }
      } catch (error) {
        console.error(
          'Error parsing stored items from localStorage FOR Save For Later:',
          error
        );
      }
    }
  };

  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (isInitialLoad.current) {
      loadFromLocalStorage();
      isInitialLoad.current = false;
    }
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      addItemToSaveForLater,
      removeSaveForLaterItem,
      onAddToCart,
      onAddToSaveForLater,
      addingToCart,
      setSaveForLaterItemToAddToCart,
    }),
    [addItemToSaveForLater, removeSaveForLaterItem, state]
  );

  return (
    <SaveForLaterContext.Provider value={value}>
      {children}
    </SaveForLaterContext.Provider>
  );
};

export const useSaveForLater = () => {
  const context = useContext(SaveForLaterContext);
  if (context === undefined) {
    throw new Error(
      `useSaveForLater must be used within a SaveForLaterProvider. Make sure to wrap your component tree with a SaveForLaterProvider to use this hook.`
    );
  }
  return context;
};
